import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { type ThemeText, useTheme } from 'lola-framework-ui-test';
import { useStore } from '../store/globalState';
import { useSessionStore } from '../store/useSessionStore';
import { usePartnerStore } from '../store/usePartnerStore';
import { useRequestStore } from '../store/useRequestStore';
import { useDeferredOriginationStore } from '../store/useDeferredOriginationStore';
import { useSendStore } from '../store/useSendStore';
import { useComposeStore } from '../store/useComposeStore';
import { RequestStatus } from '../utils/commons';
import { useGalleryStore } from './galleryStore';
import { type RawTheme } from './defaultTheme';
import {
  mockAmount,
  mockCards,
  mockQuote,
  mockSelectedPayment,
  mockSession,
  mockTransactions,
} from './mockData';
import { galleryTermsLang, getMockTerms } from './mockTerms';
import {
  applyGalleryPayoutEdit,
  GALLERY_AMOUNT_EDIT_PARAM,
  GALLERY_CURRENCY_EDIT_PARAM,
  GALLERY_PAYOUT_EDIT_PARAM,
  revertGalleryPayoutEdit,
} from './fundRequestMock';
import { requestMockDraft, requestMockMintResult } from './requestMock';
import {
  deferredOriginationMockComposeDraft,
  deferredOriginationMockDraft,
  deferredOriginationMockMintResult,
} from './deferredOriginationMock';
import {
  sendComposeSeed,
  sendMockDraft,
  sendMockQuote,
} from './sendMock';
import { smartMockPartner } from './smartMock';
import { useCashDepositStore } from '../store/useCashDepositStore';
import { useSessionStore as useSmartSessionStore } from '../components/smart/store/useSessionStore';

/**
 * Feeds the animated highlight (`.rainbow-ready` / `.payment-required`) and
 * native checkbox `accent-color` with the partner palette. Only flat colors
 * are usable here — `whiteView.backgroundBtn` is `primaryMesh`, a gradient string.
 */
const DISABLED_BUTTON_BG = '#979797';

const applyBrandSweep = (colors: RawTheme['colors']) => {
  const root = document.documentElement;
  const rawInactive = colors?.inactived;
  const disabledBg =
    typeof rawInactive === 'string' && rawInactive.trim()
      ? rawInactive.trim()
      : DISABLED_BUTTON_BG;
  const stops: Record<string, unknown> = {
    '--brand-sweep-base': colors?.primaryGradient,
    '--brand-sweep-accent': colors?.secondaryGradient,
    '--brand-sweep-highlight': colors?.partnerHighlights || colors?.whiteColor,
    '--text-secondary': colors?.secondaryColor,
    '--btn-disabled-bg': disabledBg,
    // `.loader-spinner` arc; unset until a theme lands so the CSS fallback keeps
    // the neutral grey on an unthemed splash.
    '--spinner-start': colors?.primaryGradient,
    '--spinner-end': colors?.secondaryGradient,
  };

  for (const [name, value] of Object.entries(stops)) {
    if (typeof value === 'string' && value) root.style.setProperty(name, value);
  }
};

/**
 * Reads the current raw theme from the gallery store, derives the per-view
 * color config via the lola framework and pushes it into the global store so
 * every screen re-renders with the new palette. Returns the font descriptor
 * for the `<Page>` wrapper.
 */
export const useApplyTheme = () => {
  const rawTheme = useGalleryStore((s) => s.rawTheme);
  const setTheme = useStore((s) => s.setTheme);
  const { generateColorsByView } = useTheme(rawTheme.font as ThemeText);

  useEffect(() => {
    const colorsConfig = generateColorsByView(rawTheme.colors);
    if (colorsConfig) setTheme(colorsConfig);
    applyBrandSweep(rawTheme.colors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTheme]);

  return {
    cdn: rawTheme.font.fontcdn,
    name: rawTheme.font.fontfamily,
  };
};

/**
 * Seeds all global stores with static mock data once, so each screen renders
 * fully without any API. Safe to mount at the app root. Runs as a layout
 * effect so per-screen overrides (`useGalleryPayoutEdit`) land after the seed
 * and still before the first paint.
 */
export const useSeedMockData = () => {
  const seeded = useRef(false);
  const { i18n } = useTranslation();

  useLayoutEffect(() => {
    if (seeded.current) return;
    seeded.current = true;

    useSessionStore.getState().setSession({
      data: {
        ...mockSession,
        terms: getMockTerms(i18n.resolvedLanguage ?? i18n.language),
      },
      status: RequestStatus.LOADED,
      error: null,
    });

    usePartnerStore.getState().setPartnerInfo({
      name: mockSession.partner.name,
      sessionExpiredRedirectUrl: mockSession.partner.sessionExpiredRedirectUrl,
    });

    const store = useStore.getState();
    store.setCardList(mockCards);
    store.setPaymentState({ payment: mockSelectedPayment });
    store.setAmountState({ amount: mockAmount });
    store.setTransactions(mockTransactions);
    store.setQuoteState(mockQuote);

    // Request flow: seed a composed draft + minted link so the Minting and
    // Request Ready screens render with content (no fund-request API).
    const requestStore = useRequestStore.getState();
    requestStore.setDraft(requestMockDraft);
    requestStore.setMintResult(requestMockMintResult);

    // Deferred origination: send-shaped compose + mint result for share screens.
    const deferredStore = useDeferredOriginationStore.getState();
    deferredStore.setComposeDraft(deferredOriginationMockComposeDraft);
    deferredStore.setDraft(deferredOriginationMockDraft);
    deferredStore.setMintResult(deferredOriginationMockMintResult);
    deferredStore.setPendingRecipientName(
      deferredOriginationMockDraft.recipientName
    );

    // Send flow: seed compose state + draft/quote for Transfer / Sending screens.
    const compose = useComposeStore.getState();
    compose.setOriginCountry(sendComposeSeed.originCountry);
    compose.setDestinationCountry(sendComposeSeed.destinationCountry);
    compose.setFunding(sendComposeSeed.funding);
    compose.setFundingInstrument(sendComposeSeed.fundingInstrument);
    compose.setSelection(sendComposeSeed.selection);
    compose.setRecipient(sendComposeSeed.recipient);
    compose.setBookDestinations(sendComposeSeed.bookDestinations);

    const sendStore = useSendStore.getState();
    sendStore.setDraft(sendMockDraft);
    sendStore.setQuoteState(sendMockQuote);

    // Smart flow: seed gallery session stub (partner chrome). Screens read
    // static data from smartMock; no APIs / claim / CIP.
    useSmartSessionStore.getState().setPartner({
      name: smartMockPartner.name,
      headerName: smartMockPartner.headerName,
    });

    // Cash-deposit: the merchant stays unpicked, mirroring the widget — the
    // barcode is only valid at the chain the user taps on StoresPage.
    useCashDepositStore.getState().setSelectedMerchantId(null);
  }, []);
};

/**
 * Keeps mock `session.terms` aligned with the Control Dock language.
 * Mirrors production `useTerms` (refetch on lang change) without an API:
 * swaps the authored en/es/fr gallery block into the session. Layout effect
 * so Welcome paints localized after seed / after a mockSession revert.
 */
export const useGalleryTerms = () => {
  const { i18n } = useTranslation();
  const updateSession = useSessionStore((s) => s.updateSession);
  const sessionTermsLang = useSessionStore((s) => s.session.data?.terms?.language);
  const lang = galleryTermsLang(i18n.resolvedLanguage ?? i18n.language);

  useLayoutEffect(() => {
    const terms = getMockTerms(lang);
    const current = useSessionStore.getState().session.data;
    if (!current) return;
    if (current.terms?.language === terms.language) return;
    updateSession({ ...current, terms });
  }, [lang, sessionTermsLang, updateSession]);
};

/**
 * Both fund-request Transfer entries resolve to the same route, so the
 * compose variants are selected by query flags and undone on every other
 * screen — session and compose are shared across the gallery.
 *
 * Runs as a layout effect after `useSeedMockData` so the seed cannot clobber
 * the override on a hard load, and the screen paints already flipped.
 */
export const useGalleryPayoutEdit = () => {
  const { search } = useLocation();

  useLayoutEffect(() => {
    const params = new URLSearchParams(search);
    const amount =
      params.get(GALLERY_AMOUNT_EDIT_PARAM) === '1';
    const payout =
      params.get(GALLERY_PAYOUT_EDIT_PARAM) === '1';
    const currency =
      params.get(GALLERY_CURRENCY_EDIT_PARAM) === '1';
    if (amount || payout || currency) {
      applyGalleryPayoutEdit({ amount, payout, currency });
    }
    else revertGalleryPayoutEdit();
  }, [search]);
};

/**
 * Parses and validates a pasted theme.json string.
 * Returns the parsed RawTheme or throws an Error with a friendly message.
 */
export const parseRawTheme = (raw: string): RawTheme => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON: check for trailing commas or quotes.');
  }

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Theme must be a JSON object.');
  }

  const candidate = parsed as Partial<RawTheme>;

  if (!candidate.colors || typeof candidate.colors !== 'object') {
    throw new Error('Missing "colors" object in theme.');
  }
  if (!candidate.font || typeof candidate.font !== 'object') {
    throw new Error('Missing "font" object in theme.');
  }
  const font = candidate.font as RawTheme['font'];
  if (typeof font.fontfamily !== 'string') {
    throw new Error('Theme "font.fontfamily" must be a string.');
  }
  if (typeof font.fontcdn !== 'string') {
    throw new Error('Theme "font.fontcdn" must be a string (can be empty).');
  }

  return parsed as RawTheme;
};
